import { useComparer } from '@/hooks/useComparer';
import {
  CategorySummary,
  ComparerResults,
  ComparerSummaryID,
  ComparerSummary as ComparerSummaryType
} from '@/types/comparer';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { Button, Chip, FormControlLabel, IconButton, Switch, Tooltip, Typography } from '@mui/material';
import { Box, Stack, useTheme } from '@mui/system';
import { FC, useEffect, useMemo } from 'react';
import {
  commonCategories,
  getChangedItems,
  getNodesSummary,
  getPromptsSummary,
  getVariablesSummary,
  summaryHasChanges,
  toSummary
} from '../comparerUtils/comparerUtils';

const SummaryChips: FC<{ label: string; values: CategorySummary }> = ({ label, values }) => (
  <>
    <Typography variant='button' color='text.secondary' sx={{ textTransform: 'capitalize' }}>
      {label}
    </Typography>
    <Stack direction='row' alignSelf='flex-start' gap={0.5}>
      {values.added > 0 && <Chip label={`+${values.added}`} color='success' size='small' />}
      {values.removed > 0 && <Chip label={`-${values.removed}`} color='error' size='small' />}
      {values.modified > 0 && <Chip label={`~${values.modified}`} color='warning' size='small' />}
    </Stack>
  </>
);

type ComparerSummaryProps = {
  labelA: string;
  labelB: string;
  results: ComparerResults | undefined;
  selected: ComparerSummaryID | undefined;
  setSelected: (newSelected: ComparerSummaryID) => void;
  handleSwap: () => void;
};

const ComparerSummary: FC<ComparerSummaryProps> = ({ labelA, labelB, results, selected, setSelected, handleSwap }) => {
  const theme = useTheme();
  const { showUnchanged, setShowUnchanged, expandAll, setExpandAll } = useComparer();

  const summary = useMemo<ComparerSummaryType[] | undefined>(() => {
    if (!results) return;

    const metadataChanges = results.metadata.filter((metadata) => metadata.changed);
    const metadata = { added: 0, removed: 0, modified: metadataChanges.length };

    const common = commonCategories.map(({ key, label }) => ({ label, values: toSummary(results[key]) }));
    const commonValues = common.reduce(
      (total, category) => ({
        added: total.added + category.values.added,
        removed: total.removed + category.values.removed,
        modified: total.modified + category.values.modified
      }),
      { added: 0, removed: 0, modified: 0 }
    );

    return [
      { id: 'metadata', values: metadata },
      { id: 'subroutines', values: toSummary(results.subroutines) },
      { id: 'nodes', values: getNodesSummary(results.subroutines) },
      { id: 'variables', values: getVariablesSummary(results.variables) },
      { id: 'prompts', values: getPromptsSummary(results.prompts) },
      { id: 'common', values: commonValues, categories: common },
      { id: 'folders', values: toSummary(results.folders) }
    ];
  }, [results]);

  const categoryHasChanges = (category: ComparerSummaryType): boolean => {
    if (category.id === 'prompts' && results && getChangedItems(results.prompts.groups).length) return true;
    return summaryHasChanges(category.values);
  };

  useEffect(() => {
    if (!summary) return;

    const visible = showUnchanged ? summary : summary.filter(categoryHasChanges);
    if (selected && visible.some(({ id }) => id === selected)) return;

    const changed = visible.filter(categoryHasChanges);
    const categories = changed.length ? changed : visible;
    const subroutines = categories.find(({ id }) => id === 'subroutines');
    setSelected(subroutines?.id ?? categories[0]?.id ?? 'empty');
  }, [summary, showUnchanged]);

  return (
    <Stack direction='row' width={'100%'} justifyContent='space-between'>
      <Stack direction='row' gap={1} mb={1} overflow='auto'>
        {summary?.map((category) => {
          if (!categoryHasChanges(category) && !showUnchanged) return;
          const isSelected = selected === category.id;
          return (
            <Button
              key={category.id}
              name={category.id}
              onClick={(e) => setSelected(e.currentTarget.name as ComparerSummaryID)}
              size='small'
              sx={{
                minWidth: 'fit-content',
                flexDirection: 'column',
                flex: 1,
                border: isSelected ? 3 : 1,
                borderColor: isSelected ? theme.palette.grey[600] : 'divider',
                borderRadius: 3,
                px: 1.5,
                my: 1
              }}
            >
              {category.categories ? (
                <Stack direction='row' gap={1.5}>
                  {category.categories
                    .filter((subCategory) => showUnchanged || summaryHasChanges(subCategory.values))
                    .map((subCategory) => (
                      <Stack key={subCategory.label} alignItems='center'>
                        <SummaryChips label={subCategory.label} values={subCategory.values} />
                      </Stack>
                    ))}
                </Stack>
              ) : (
                <SummaryChips label={category.id} values={category.values} />
              )}
            </Button>
          );
        })}
      </Stack>
      <Stack flexDirection='row' justifyContent='space-between' alignItems='baseline' alignSelf='baseline' mx={2}>
        <Box color='error.main'>
          <Typography variant='body1'>{labelA}</Typography>
        </Box>
        <Tooltip title='Swap A and B'>
          <span style={{ height: 'fit-content' }}>
            <IconButton onClick={handleSwap} size='large' color='primary'>
              <SwapHorizIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Box color='success.main'>
          <Typography variant='body1'>{labelB}</Typography>
        </Box>
      </Stack>
      <Stack alignSelf='baseline' alignItems='flex-start'>
        <FormControlLabel
          control={<Switch size='small' checked={showUnchanged} onChange={(e) => setShowUnchanged(e.target.checked)} />}
          label={<Typography variant='caption'>Show unchanged</Typography>}
        />
        <FormControlLabel
          control={
            <Switch size='small' checked={expandAll ?? false} onChange={(e) => setExpandAll(e.target.checked)} />
          }
          label={<Typography variant='caption'>Expand all</Typography>}
        />
      </Stack>
    </Stack>
  );
};

export default ComparerSummary;
